import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { cn } from "./ui/utils";
import { BookOpen, Users, Calendar, TrendingUp, Award, Clock, Briefcase, DollarSign, MessageSquare, Activity, BarChart2, Target } from "lucide-react";
import { useState, useEffect } from "react";
import API from "./api/api01";

// Import your pages/components
import { Attendance } from "./student/Attendance";
import { Examination } from "./student/Examination";
import { Library } from "./student/Library";
import { LeadManagement } from "./admin/LeadManagement";
import { LeadAll } from "./admin/LeadAll";
import { LeadContact } from "./admin/LeadContact";
import { LeadLost } from "./admin/LeadLost";
import { LeadActivities } from "./admin/LeadActivities";
import { LeadAccounts } from "./admin/LeadAccounts";
import { CampaignDashboard } from "./admin/CampaignDashboard";
import SocialDashboard from "./superadmin/SocialDashboard";
import { Reports } from "./admin/Reports";
import { SuperadminDashboard } from "./SuperadminDashboard";


interface DashboardProps {
  user: {
    name: string;
    role: "Student" | "Teacher" | "Admin" | "Superadmin" | "Client" | "Customer" | null;
  };
}

export function Dashboard({ user }: DashboardProps) {
  const [currentPage, setCurrentPage] = useState<string>("dashboard");
  const [clientStats, setClientStats] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchStats = user.role === "Client" || user.role === "Customer";

        // Always fetch activities if role is defined
        const endpoints = [API.get("/dashboard/activity")];
        if (fetchStats) endpoints.push(API.get("/dashboard/stats"));

        const results = await Promise.all(endpoints);
        setActivities(results[0].data || []);
        if (fetchStats && results[1]) setClientStats(results[1].data);

      } catch (err) {
        console.error("Dashboard fetch error:", err);
      }
    };
    if (user.role) fetchData();
  }, [user.role]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  const getIconByName = (name: string) => {
    switch (name) {
      case "Users": return Users;
      case "TrendingUp": return TrendingUp;
      case "Award": return Award;
      case "Activity": return Activity;
      case "Briefcase": return Briefcase;
      case "DollarSign": return DollarSign;
      case "MessageSquare": return MessageSquare;
      case "Calendar": return Calendar;
      case "BookOpen": return BookOpen;
      case "Clock": return Clock;
      case "BarChart2": return BarChart2;
      case "Target": return Target;
      default: return Activity;
    }
  };

  const getStatsForRole = () => {
    if (user.role === "Student") {
      return [
        { label: "Courses Enrolled", value: "6", icon: BookOpen, color: "text-primary" },
        { label: "Attendance", value: "92%", icon: Calendar, color: "text-primary" },
        { label: "Average Grade", value: "A-", icon: Award, color: "text-chart-3" },
        { label: "Pending Tasks", value: "4", icon: Clock, color: "text-chart-4" },
      ];
    }

    if (user.role === "Teacher") {
      return [
        { label: "Total Students", value: "156", icon: Users, color: "text-primary" },
        { label: "Classes Today", value: "3", icon: Calendar, color: "text-primary" },
        { label: "Assignments Pending", value: "12", icon: Clock, color: "text-chart-3" },
        { label: "Average Performance", value: "B+", icon: TrendingUp, color: "text-chart-2" },
      ];
    }

    if (user.role === "Client" || user.role === "Customer") {
      if (clientStats && clientStats.length > 0) {
        return clientStats.map(s => ({
          ...s,
          icon: getIconByName(s.icon)
        }));
      }
      return [
        { label: "Active Campaigns", value: "0", icon: Target, color: "text-primary" },
        { label: "Total Leads", value: "0", icon: Users, color: "text-chart-3" },
        { label: "Account Status", value: "Active", icon: Activity, color: "text-chart-2" },
      ];
    }

    return [
      { label: "Total Users", value: "1,234", icon: Users, color: "text-primary" },
      { label: "Active Teachers", value: "45", icon: BookOpen, color: "text-primary" },
      { label: "Total Students", value: "1,189", icon: Users, color: "text-chart-3" },
      { label: "System Health", value: "98%", icon: TrendingUp, color: "text-chart-2" },
    ];
  };

  const stats = (user.role === "Client" || user.role === "Customer") && clientStats.length > 0
    ? clientStats.map(s => ({ ...s, icon: getIconByName(s.icon) }))
    : getStatsForRole();

  const getRoleBadgeColor = () => {
    if (user.role === "Superadmin") return "bg-purple-600 text-white";
    if (user.role === "Admin") return "bg-chart-4 text-white";
    if (user.role === "Teacher") return "bg-primary text-primary-foreground";
    return "bg-secondary text-secondary-foreground";
  };

  // Handle navigation
  const handleNavigate = (page: string) => setCurrentPage(page);

  // 🛡️ Superadmin Premium Dashboard Redirection
  if (user.role === "Superadmin" && currentPage === "dashboard") {
    return <SuperadminDashboard user={user as any} />;
  }

  // If a page is selected, show it with Back button
  if (currentPage !== "dashboard") {
    let PageComponent;
    switch (currentPage) {
      case "attendance":
        PageComponent = Attendance;
        break;
      case "grades":
        PageComponent = Examination;
        break;
      case "library":
        PageComponent = Library;
        break;
      case "lead-tracking":
        PageComponent = LeadManagement;
        break;
      case "campaign-dashboard":
        PageComponent = CampaignDashboard;
        break;
      case "lead-contact":
        PageComponent = LeadContact;
        break;
      case "lead-lost":
        PageComponent = LeadLost;
        break;
      case "lead-all":
        PageComponent = LeadAll;
        break;
      case "lead-activities":
        PageComponent = LeadActivities;
        break;
      case "lead-accounts":
        PageComponent = LeadAccounts;
        break;
      case "social-dashboard":
        PageComponent = SocialDashboard;
        break;
      case "reports":
        PageComponent = Reports;
        break;
      default:
        PageComponent = null;
    }

    return (
      <div className="p-6 max-w-7xl mx-auto">
        <button
          onClick={() => setCurrentPage("dashboard")}
          className="mb-4 px-4 py-2 bg-primary text-white rounded hover:bg-primary/80 transition"
        >
          ← Back to Dashboard
        </button>
        {PageComponent && <PageComponent />}
      </div>
    );
  }

  const formatActivityTime = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays === 1) return "Yesterday";
      return date.toLocaleDateString();
    } catch (e) {
      return timestamp;
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto bg-background min-h-screen">
      {/* Greeting Header */}
      <div
        className="space-y-2"
        style={{ animation: 'fadeInDown 0.45s ease both' }}
      >
        <h1 className="text-3xl font-semibold tracking-tight">{getGreeting()}, {user.name}! 👋</h1>
        <div className="flex items-center gap-2">
          <p className="text-muted-foreground">Welcome to your dashboard</p>
          <Badge className={getRoleBadgeColor()}>{user.role}</Badge>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          const iconBgColors = [
            "bg-blue-500/10 text-blue-500",
            "bg-emerald-500/10 text-emerald-500",
            "bg-purple-500/10 text-purple-500",
            "bg-orange-500/10 text-orange-500",
          ];
          return (
            <Card
              key={index}
              className="glass-card group shadow-none dark:shadow-md"
              style={{
                animation: 'scaleIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) both',
                animationDelay: `${index * 100 + 100}ms`,
              }}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{stat.label}</CardTitle>
                <div className={cn("p-2 rounded-xl transition-colors", iconBgColors[index % 4])}>
                  <Icon className="h-5 w-5" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold tracking-tight">{stat.value}</div>
                <div className="flex items-center gap-1 mt-2">
                  <span className="text-xs font-medium text-emerald-500">↑ 0%</span>
                  <span className="text-[10px] text-muted-foreground">vs last period</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <Card
        className="glass-card"
        style={{ animation: 'fadeInUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) both', animationDelay: '420ms' }}
      >
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {user.role === "Student" && (
              <>
                <QuickActionCard title="View Attendance" description="Check your attendance records" onClick={() => handleNavigate("attendance")} />
                <QuickActionCard title="Download Documents" description="Access course materials" onClick={() => handleNavigate("library")} />
                <QuickActionCard title="View Grades" description="See your academic performance" onClick={() => handleNavigate("grades")} />
              </>
            )}
            {user.role === "Teacher" && (
              <>
                <QuickActionCard title="Mark Attendance" description="Record student attendance" onClick={() => handleNavigate("attendance")} />
                <QuickActionCard title="Grade Assignments" description="Review and grade submissions" onClick={() => handleNavigate("grades")} />
                <QuickActionCard title="Manage Classes" description="View class schedules" onClick={() => handleNavigate("classes")} />
              </>
            )}
            {user.role === "Admin" && (
              <>
                <QuickActionCard title="Manage Users" description="Add or modify user accounts" onClick={() => handleNavigate("users")} />
                <QuickActionCard title="Process Salaries" description="Handle salary payments" onClick={() => handleNavigate("salaries")} />
                <QuickActionCard title="System Reports" description="View analytics and reports" onClick={() => handleNavigate("reports")} />
              </>
            )}
            {(user.role === "Client" || user.role === "Customer" || user.role === "Superadmin") && (
              <>
                <QuickActionCard title="Lead Tracking" description="Monitor your leads and statuses" onClick={() => handleNavigate("lead-tracking")} />
                <QuickActionCard title="Campaigns" description="View campaign performance" onClick={() => handleNavigate("campaign-dashboard")} />
                <QuickActionCard title="Activity Log" description="Review detailed lead interactions" onClick={() => handleNavigate("lead-activities")} />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card
        className="glass-card"
        style={{ animation: 'fadeInUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) both', animationDelay: '520ms' }}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">Recent Activity</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => handleNavigate("lead-activities")} className="text-xs text-primary hover:bg-primary/10">View All</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {activities.length > 0 ? (
              activities.slice(0, 6).map((act, i) => (
                <ActivityItem
                  key={act.id || i}
                  time={formatActivityTime(act.time)}
                  text={act.text}
                  type={act.activity_type}
                  leadName={act.lead_name}
                />
              ))
            ) : (
              <div className="text-center py-6">
                <p className="text-muted-foreground">No recent activity found</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// QuickActionCard updated to accept onClick
function QuickActionCard({ title, description, onClick }: { title: string; description: string; onClick?: () => void }) {
  return (
    <div
      onClick={onClick}
      className="p-4 rounded-xl border border-border-subtle hover:bg-sidebar-accent hover:border-primary/30 transition-all duration-300 cursor-pointer group shadow-none dark:shadow-sm hover:dark:shadow-md"
    >
      <h4 className="font-semibold group-hover:text-primary transition-colors">{title}</h4>
      <p className="text-xs text-muted-foreground mt-1.5">{description}</p>
    </div>
  );
}

function ActivityItem({ time, text, type, leadName }: { time: string; text: string; type?: string; leadName?: string }) {
  const getIcon = () => {
    switch (type) {
      case "call": return <Phone className="h-4 w-4 text-blue-500" />;
      case "email": return <Mail className="h-4 w-4 text-purple-500" />;
      case "registration": return <Target className="h-4 w-4 text-green-500" />;
      default: return <Clock className="h-4 w-4 text-slate-400" />;
    }
  };

  return (
    <div className="flex items-start gap-4 pb-4 border-b border-border-subtle last:border-0 last:pb-0 group hover:bg-sidebar-accent/50 p-3 rounded-xl transition-all duration-300">
      <div className="p-2.5 rounded-xl bg-sidebar-accent group-hover:bg-card transition-colors shadow-inner">
        {getIcon()}
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">{text}</p>
          <span className="text-[10px] text-muted-foreground uppercase">{time}</span>
        </div>
        {leadName && <p className="text-xs text-muted-foreground mt-0.5">Lead: {leadName}</p>}
      </div>
    </div>
  );
}


function Phone({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
  );
}

function Mail({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
  );
}
